import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DeviceMobile, DownloadSimple, X, Share, Plus } from '@phosphor-icons/react';
import { Button } from './ui/button';

const DISMISS_KEY = 'install_prompt_dismissed_until';
const DISMISS_DAYS = 7;
const FIRST_SHOW_DELAY_MS = 8000;

// Module-level so the deferred BIP event survives unmounts and is shareable
// with the manual <InstallAppButton />.
let _deferredPrompt = null;
const _bipListeners = new Set();

if (typeof window !== 'undefined') {
    window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault();
        _deferredPrompt = e;
        _bipListeners.forEach((cb) => { try { cb(e); } catch { /* ignore */ } });
    });
    window.addEventListener('appinstalled', () => {
        _deferredPrompt = null;
        _bipListeners.forEach((cb) => { try { cb(null); } catch { /* ignore */ } });
    });
}

function isiOS() {
    if (typeof navigator === 'undefined') return false;
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isStandalone() {
    if (typeof window === 'undefined') return false;
    return (
        window.matchMedia?.('(display-mode: standalone)').matches ||
        window.navigator.standalone === true
    );
}

export default function InstallAppPrompt() {
    const [deferred, setDeferred] = useState(_deferredPrompt);
    const [visible, setVisible] = useState(false);
    const [showIosHelp, setShowIosHelp] = useState(false);
    const ios = isiOS();

    useEffect(() => {
        if (isStandalone()) return; // Already installed — never show
        try {
            const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
            if (Date.now() < until) return; // Dismissed recently
        } catch { /* ignore */ }

        const cb = (e) => {
            setDeferred(e);
            window.setTimeout(() => setVisible(true), FIRST_SHOW_DELAY_MS);
        };
        _bipListeners.add(cb);
        if (_deferredPrompt) cb(_deferredPrompt);

        // iOS Safari: no beforeinstallprompt → show our manual tip
        if (ios && !isStandalone()) {
            const t = window.setTimeout(() => setVisible(true), FIRST_SHOW_DELAY_MS);
            return () => {
                _bipListeners.delete(cb);
                window.clearTimeout(t);
            };
        }
        return () => { _bipListeners.delete(cb); };
    }, [ios]);

    const dismiss = (days = DISMISS_DAYS) => {
        try {
            localStorage.setItem(DISMISS_KEY, String(Date.now() + days * 86400000));
        } catch { /* ignore */ }
        setVisible(false);
        setShowIosHelp(false);
    };

    const install = async () => {
        if (deferred) {
            try {
                deferred.prompt();
                const choice = await deferred.userChoice;
                if (choice?.outcome === 'accepted') {
                    dismiss(365); // Long dismiss after install
                } else {
                    dismiss(); // Short dismiss on cancel
                }
            } catch {
                dismiss();
            }
            setDeferred(null);
        } else if (ios) {
            setShowIosHelp(true);
        }
    };

    if (!visible) return null;

    return (
        <>
            <AnimatePresence>
                {visible && !showIosHelp && (
                    <motion.div
                        initial={{ y: 80, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 80, opacity: 0 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 26 }}
                        className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-[55] bg-[#2D3748] border border-[#D4A537]/40 rounded-2xl shadow-2xl backdrop-blur-sm overflow-hidden"
                        data-testid="install-prompt"
                    >
                        <div className="px-4 py-3 sm:px-5 sm:py-4">
                            <div className="flex items-start gap-3">
                                <div className="p-2 rounded-xl bg-[#D4A537]/15 border border-[#D4A537]/40 text-[#D4A537] flex-shrink-0">
                                    <DeviceMobile size={20} weight="fill" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                        Installeer AutoPilot
                                    </div>
                                    <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">
                                        Voeg toe aan je beginscherm voor snelle toegang en een app-ervaring.
                                    </p>
                                </div>
                                <button
                                    onClick={() => dismiss()}
                                    className="text-gray-500 hover:text-gray-300 transition-colors p-1 -m-1 flex-shrink-0"
                                    data-testid="install-dismiss"
                                    aria-label="Sluiten"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                            <div className="flex gap-2 mt-3">
                                <Button
                                    onClick={() => dismiss()}
                                    variant="ghost"
                                    className="flex-1 text-gray-400 hover:text-white text-xs h-9"
                                    data-testid="install-later"
                                >
                                    Later
                                </Button>
                                <Button
                                    onClick={install}
                                    className="flex-1 bg-[#D4A537] hover:bg-[#E5B84A] text-[#1A202C] font-semibold text-xs h-9"
                                    data-testid="install-install"
                                >
                                    <DownloadSimple size={14} weight="bold" className="mr-1.5" />
                                    Installeer
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* iOS step-by-step modal */}
            <AnimatePresence>
                {showIosHelp && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm"
                        onClick={() => setShowIosHelp(false)}
                        data-testid="install-ios-modal"
                    >
                        <motion.div
                            initial={{ scale: 0.94, y: 20, opacity: 0 }}
                            animate={{ scale: 1, y: 0, opacity: 1 }}
                            exit={{ scale: 0.94, y: 20, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full max-w-sm bg-[#2D3748] border border-[#4A5568] rounded-2xl shadow-2xl overflow-hidden"
                        >
                            <div className="px-5 pt-5 pb-3 border-b border-[#4A5568] flex items-start justify-between">
                                <div>
                                    <h2 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                        Installeren op iPhone
                                    </h2>
                                    <p className="text-xs text-gray-400 mt-0.5">In 2 stappen op je beginscherm</p>
                                </div>
                                <button onClick={() => setShowIosHelp(false)} className="text-gray-500 hover:text-white p-1 -m-1">
                                    <X size={18} />
                                </button>
                            </div>
                            <ol className="px-5 py-4 space-y-3 text-sm text-gray-200">
                                <li className="flex items-start gap-3">
                                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#D4A537]/20 text-[#D4A537] font-bold text-xs flex items-center justify-center">1</span>
                                    <span>Tik in Safari onderin op het <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#1A202C] border border-[#4A5568]"><Share size={12} className="inline mr-1" /> Deel</span>-icoon.</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#D4A537]/20 text-[#D4A537] font-bold text-xs flex items-center justify-center">2</span>
                                    <span>Kies <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#1A202C] border border-[#4A5568]"><Plus size={12} className="inline mr-1" /> Zet op beginscherm</span> en bevestig met "Voeg toe".</span>
                                </li>
                            </ol>
                            <div className="px-5 pb-5">
                                <Button
                                    onClick={() => dismiss()}
                                    className="w-full bg-[#D4A537] hover:bg-[#E5B84A] text-[#1A202C] font-semibold"
                                    data-testid="install-ios-done"
                                >
                                    Komt goed!
                                </Button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}


/**
 * Always-available header button to install the app manually.
 * - On Chrome/Android: triggers the deferred beforeinstallprompt event
 * - On iOS Safari: opens the Share → Add to Home Screen instruction modal
 * - Hidden once the app is already installed (display-mode: standalone)
 */
export function InstallAppButton({ className = '' }) {
    const [deferred, setDeferred] = useState(_deferredPrompt);
    const [showIosHelp, setShowIosHelp] = useState(false);
    const [hidden, setHidden] = useState(false);
    const ios = isiOS();

    useEffect(() => {
        // Hide if already installed
        if (isStandalone()) { setHidden(true); return; }
        const cb = (e) => setDeferred(e);
        _bipListeners.add(cb);
        return () => { _bipListeners.delete(cb); };
    }, []);

    if (hidden) return null;

    const handleClick = async () => {
        // Reset any prior dismissal so the auto-popup also unblocks
        try { localStorage.removeItem(DISMISS_KEY); } catch { /* ignore */ }
        if (deferred) {
            try {
                deferred.prompt();
                const choice = await deferred.userChoice;
                if (choice?.outcome === 'accepted') {
                    setHidden(true);
                    _deferredPrompt = null;
                }
            } catch { /* ignore */ }
        } else if (ios) {
            setShowIosHelp(true);
        } else {
            // No deferred event — typical for Chrome desktop or already-prompted state.
            // Show a generic help dialog.
            setShowIosHelp(true);
        }
    };

    return (
        <>
            <button
                onClick={handleClick}
                className={`text-gray-400 hover:text-[#D4A537] transition-colors p-2 rounded-lg hover:bg-[#3D4A5C] ${className}`}
                title="App installeren"
                aria-label="App installeren"
                data-testid="install-app-button"
            >
                <DownloadSimple size={18} weight="bold" />
            </button>

            <AnimatePresence>
                {showIosHelp && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm"
                        onClick={() => setShowIosHelp(false)}
                        data-testid="install-help-modal"
                    >
                        <motion.div
                            initial={{ scale: 0.94, y: 20, opacity: 0 }}
                            animate={{ scale: 1, y: 0, opacity: 1 }}
                            exit={{ scale: 0.94, y: 20, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full max-w-sm bg-[#2D3748] border border-[#4A5568] rounded-2xl shadow-2xl overflow-hidden"
                        >
                            <div className="px-5 pt-5 pb-3 border-b border-[#4A5568] flex items-start justify-between">
                                <div>
                                    <h2 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                        AutoPilot installeren
                                    </h2>
                                    <p className="text-xs text-gray-400 mt-0.5">Op je beginscherm zetten</p>
                                </div>
                                <button onClick={() => setShowIosHelp(false)} className="text-gray-500 hover:text-white p-1 -m-1">
                                    <X size={18} />
                                </button>
                            </div>
                            <div className="px-5 py-4 text-sm text-gray-200 space-y-3">
                                {ios ? (
                                    <>
                                        <div className="flex items-start gap-3">
                                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#D4A537]/20 text-[#D4A537] font-bold text-xs flex items-center justify-center">1</span>
                                            <span>Tik in Safari onderin op het <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#1A202C] border border-[#4A5568]"><Share size={12} className="inline mr-1" /> Deel</span>-icoon.</span>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#D4A537]/20 text-[#D4A537] font-bold text-xs flex items-center justify-center">2</span>
                                            <span>Kies <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#1A202C] border border-[#4A5568]"><Plus size={12} className="inline mr-1" /> Zet op beginscherm</span> en bevestig met "Voeg toe".</span>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <p>De automatische installatieprompt is nog niet beschikbaar in jouw browser. Je kunt het zelf doen:</p>
                                        <div className="flex items-start gap-3">
                                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#D4A537]/20 text-[#D4A537] font-bold text-xs flex items-center justify-center">1</span>
                                            <span>Tik in Chrome op het <strong>menu</strong> (3 puntjes rechtsboven).</span>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#D4A537]/20 text-[#D4A537] font-bold text-xs flex items-center justify-center">2</span>
                                            <span>Kies <strong>"App installeren"</strong> of <strong>"Aan startscherm toevoegen"</strong>.</span>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#D4A537]/20 text-[#D4A537] font-bold text-xs flex items-center justify-center">3</span>
                                            <span>Bevestig met "Installeren". De app komt vervolgens op je beginscherm.</span>
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="px-5 pb-5">
                                <Button
                                    onClick={() => setShowIosHelp(false)}
                                    className="w-full bg-[#D4A537] hover:bg-[#E5B84A] text-[#1A202C] font-semibold"
                                    data-testid="install-help-done"
                                >
                                    Komt goed!
                                </Button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
